'use client';

import type { ButtonProps, RecipeProps } from '@chakra-ui/react';
import {
  Button,
  FileUpload as ChakraFileUpload,
  Icon,
  IconButton,
  Span,
  Text,
  useFileUploadContext,
  useFileUpload,
  useRecipe,
  Code,
  Box,
  InputGroup,
  Input,
  CloseButton,
  VStack,
  Wrap,
  HStack,
} from '@chakra-ui/react';
import * as React from 'react';
import { LuFile, LuUpload, LuX, LuFileUp } from 'react-icons/lu';
import type { FileError, FileValidateDetails } from '@zag-js/file-upload';
import { ATTACHMENT_MAX_FILE_SIZE_BYTES } from '@/constants/uploadLimits';

export type FileErrorKey =
  | 'TOO_MANY_FILES'
  | 'TOO_FEW_FILES'
  | 'FILE_INVALID_TYPE'
  | 'FILE_TOO_LARGE'
  | 'FILE_TOO_SMALL'
  | 'FILE_INVALID'
  | 'FILE_EXISTS'
  | 'CUSTOM_FILTER';

export interface FileUploadRootProps extends ChakraFileUpload.RootProps {
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
}

export const FileUploadRoot = React.forwardRef<
  HTMLInputElement,
  FileUploadRootProps
>(function FileUploadRoot(props, ref) {
  const { children, inputProps, ...rest } = props;
  return (
    <ChakraFileUpload.Root {...rest}>
      <ChakraFileUpload.HiddenInput ref={ref} {...inputProps} />
      {children}
    </ChakraFileUpload.Root>
  );
});

export interface FileUploadDropzoneProps
  extends ChakraFileUpload.DropzoneProps {
  label: React.ReactNode;
  description?: React.ReactNode;
}

export const FileUploadDropzone = React.forwardRef<
  HTMLInputElement,
  FileUploadDropzoneProps
>(function FileUploadDropzone(props, ref) {
  const { children, label, description, ...rest } = props;
  return (
    <ChakraFileUpload.Dropzone ref={ref} {...rest}>
      <Icon fontSize="xl" color="fg.muted">
        <LuUpload />
      </Icon>
      <ChakraFileUpload.DropzoneContent>
        <div>{label}</div>
        {description && <Text color="fg.muted">{description}</Text>}
      </ChakraFileUpload.DropzoneContent>
      {children}
    </ChakraFileUpload.Dropzone>
  );
});

interface VisibilityProps {
  showSize?: boolean;
  clearable?: boolean;
}

interface FileUploadItemProps extends VisibilityProps {
  file: File;
}

const FileUploadItem = React.forwardRef<HTMLLIElement, FileUploadItemProps>(
  function FileUploadItem(props, ref) {
    const { file, showSize, clearable } = props;
    return (
      <ChakraFileUpload.Item
        py={1}
        px={2}
        file={file}
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="xs"
        color="red"
        bg="bg.subtle"
        borderColor="gray.muted"
        ref={ref}
      >
        <ChakraFileUpload.ItemPreview asChild>
          <Icon>
            <LuFile />
          </Icon>
        </ChakraFileUpload.ItemPreview>

        {showSize ? (
          <ChakraFileUpload.ItemContent
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
          >
            <ChakraFileUpload.ItemName color="gray.fg" />
            <ChakraFileUpload.ItemSizeText
              color="gray.fg"
              fontStyle={'italic'}
            />
          </ChakraFileUpload.ItemContent>
        ) : (
          <ChakraFileUpload.ItemName flex="1" />
        )}

        {clearable && (
          <ChakraFileUpload.ItemDeleteTrigger alignSelf="center" asChild>
            <IconButton variant="ghost" color="fg.muted" size="xs">
              <LuX />
            </IconButton>
          </ChakraFileUpload.ItemDeleteTrigger>
        )}
      </ChakraFileUpload.Item>
    );
  },
);

interface FileUploadListProps
  extends VisibilityProps, ChakraFileUpload.ItemGroupProps {
  files?: File[];
}

export const FileUploadList = React.forwardRef<
  HTMLUListElement,
  FileUploadListProps
>(function FileUploadList(props, ref) {
  const {
    showSize: showSizeProp,
    clearable: clearableProp,
    files,
    ...rest
  } = props;

  const fileUpload = useFileUploadContext();
  const acceptedFiles = files ?? fileUpload.acceptedFiles;

  if (acceptedFiles.length === 0) return null;

  return (
    <ChakraFileUpload.ItemGroup ref={ref} {...rest}>
      {acceptedFiles.map((file) => (
        <FileUploadItem
          key={file.name}
          file={file}
          showSize={showSizeProp}
          clearable={clearableProp}
        />
      ))}
    </ChakraFileUpload.ItemGroup>
  );
});

type Assign<T, U> = Omit<T, keyof U> & U;

interface FileInputProps extends Assign<ButtonProps, RecipeProps<'input'>> {
  placeholder?: React.ReactNode;
}

export const FileInput = React.forwardRef<HTMLButtonElement, FileInputProps>(
  function FileInput(props, ref) {
    const inputRecipe = useRecipe({ key: 'input' });
    const [recipeProps, restProps] = inputRecipe.splitVariantProps(props);
    const { placeholder = 'Select file(s)', ...rest } = restProps;
    return (
      <ChakraFileUpload.Trigger asChild>
        <Button
          unstyled
          py="0"
          ref={ref}
          {...rest}
          css={[inputRecipe(recipeProps), props.css]}
        >
          <ChakraFileUpload.Context>
            {({ acceptedFiles }) => {
              if (acceptedFiles.length === 1) {
                return <span>{acceptedFiles[0].name}</span>;
              }
              if (acceptedFiles.length > 1) {
                return <span>{acceptedFiles.length} files</span>;
              }
              return <Span color="fg.subtle">{placeholder}</Span>;
            }}
          </ChakraFileUpload.Context>
        </Button>
      </ChakraFileUpload.Trigger>
    );
  },
);

export const FileUploadLabel = ChakraFileUpload.Label;
export const FileUploadClearTrigger = ChakraFileUpload.ClearTrigger;
export const FileUploadTrigger = ChakraFileUpload.Trigger;
export type FileTypes =
  | 'image'
  | 'file'
  | 'excel'
  | 'spreadsheet'
  | 'pdf'
  | 'all';

export type AcceptedFileTypes = {
  [key in FileTypes]: string[];
};

export const acceptedFileFormatTypes: AcceptedFileTypes = {
  image: ['image/*'],
  file: [
    'application/pdf',
    '.pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ],
  excel: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    'application/csv',
    '.xlsx',
    '.xls',
    '.csv',
  ],
  spreadsheet: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
    '.xlsx',
    '.xls',
  ],
  pdf: ['application/pdf', '.pdf'],
  all: ['*'],
};
export interface FileUploadProps {
  onUpload: (file: File | File[]) => void; // if maxFiles is 1, then file is a File, if maxFiles is > 1, then file is an array of Files
  acceptedFormats?: AcceptedFileTypes[FileTypes];
  fileType?: FileTypes;
  label?: React.ReactNode;
  maxFiles?: number; // default 1, how many files can be uploaded
  minFiles?: number; // default undefined, how many files can be uploaded
  maxFileSize?: number; // default 100 MB, max file size in bytes
  disabled?: boolean;
  showList?: boolean;
  showSize?: boolean;
  clearable?: boolean;
  customFilter?: (
    file: File,
  ) => void | boolean | { code: string | number; message: string };
  children?: (props: { files: File[] }) => React.ReactNode; // use this to show custom display of uploaded files
  [key: string]: any;
}

export const FileUpload = ({
  onUpload,
  acceptedFormats,
  fileType = 'file',
  maxFiles = 1,
  minFiles = 0,
  maxFileSize = ATTACHMENT_MAX_FILE_SIZE_BYTES, // 100 MB
  disabled = false,
  showList = true,
  showSize = true,
  clearable = true,
  customFilter,
  children,
  inputProps,
  ...rest
}: FileUploadProps & { inputProps?: React.InputHTMLAttributes<HTMLInputElement> }) => {
  const accept = acceptedFormats
    ? acceptedFormats
    : fileType
      ? acceptedFileFormatTypes[fileType]
      : ['*'];

  const [rejectedError, setRejectedError] = React.useState<
    Partial<Record<FileErrorKey, string>>
  >({});
  const [tempCustomError, setTempCustomError] = React.useState<
    string | undefined
  >(undefined);

  // Combined function to get all error messages from rejected files
  const getErrors = (
    rejectedFiles: ChakraFileUpload.FileRejection[],
    acceptedLength: number,
    rejectedLength: number,
  ): Partial<Record<FileError, string>> => {
    const formatSize = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      if (bytes < 1024 * 1024 * 1024)
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    };

    const getErrorMessage = (errorCode: FileError): string => {
      switch (errorCode) {
        case 'TOO_MANY_FILES':
          return `You can only upload a maximum of ${maxFiles} file${maxFiles > 1 ? 's' : ''}`;
        case 'TOO_FEW_FILES':
          return `You must upload at least ${minFiles} valid file${minFiles > 1 ? 's' : ''}`;
        case 'FILE_INVALID_TYPE':
          return 'Please select a valid file type.';
        case 'FILE_TOO_LARGE':
          return `Please select a file smaller than ${formatSize(maxFileSize)}`;
        case 'FILE_INVALID':
          return 'Please select a valid file.';
        case 'FILE_EXISTS':
          return 'The file already exists.';
        case 'CUSTOM_FILTER':
          return 'Please select a valid file.';
        default:
          return `Error: ${errorCode}`;
      }
    };

    const tempErrors: Partial<Record<FileError, string>> = {};

    // Process rejected files and extract error messages
    rejectedFiles.forEach((e: ChakraFileUpload.FileRejection) => {
      e.errors.forEach((value: FileError) => {
        tempErrors[value] = getErrorMessage(value);
      });
    });

    // Add TOO_FEW_FILES error if needed
    if (
      !tempErrors['TOO_FEW_FILES'] &&
      minFiles > 0 &&
      acceptedLength < minFiles
    ) {
      tempErrors['TOO_FEW_FILES'] = getErrorMessage('TOO_FEW_FILES');
    }

    // Add TOO_MANY_FILES error if needed
    if (
      !tempErrors['TOO_MANY_FILES'] &&
      (acceptedLength > maxFiles ||
        (acceptedLength <= maxFiles &&
          acceptedLength + rejectedLength > maxFiles))
    ) {
      tempErrors['TOO_MANY_FILES'] = getErrorMessage('TOO_MANY_FILES');
    }

    if (tempCustomError || rejectedError.CUSTOM_FILTER) {
      tempErrors['CUSTOM_FILTER'] = tempCustomError;
    }

    return tempErrors;
  };

  const getFilteredFiles = async (
    files: File[],
  ): Promise<{ files: File[]; errors: string | undefined }> => {
    if (!customFilter)
      return Promise.resolve({ files: files, errors: undefined });
    const newErrors: Partial<Record<string | number, string>> = {};
    const filteredFiles = files.filter((file: File) => {
      const result:
        | boolean
        | { code: string | number; message: string }
        | void = customFilter(file);

      if (
        result &&
        typeof result === 'object' &&
        'code' in result &&
        'message' in result
      ) {
        const { code, message } = result as {
          code: string | number;
          message: string;
        };
        newErrors[code] = message;
        return false; // Reject the file
      }

      if (typeof result === 'boolean') {
        return result;
      }

      return true; // Accept the file
    });

    return Promise.resolve({
      files: filteredFiles,
      errors:
        Object.keys(newErrors).length > 0
          ? Object.values(newErrors).join(', ')
          : undefined,
    });
  };

  const fileUpload = useFileUpload({
    accept,
    maxFiles,
    maxFileSize,
    transformFiles: customFilter
      ? async (files: File[]): Promise<File[]> => {
          const { files: filteredFiles, errors } =
            await getFilteredFiles(files);
          setTempCustomError(errors);
          return Promise.resolve(filteredFiles);
        }
      : undefined,

    onFileChange: ({ acceptedFiles, rejectedFiles }) => {
      setRejectedError(
        getErrors(rejectedFiles, acceptedFiles.length, rejectedFiles.length),
      );
      setTempCustomError(undefined);
      onUpload(maxFiles === 1 ? acceptedFiles[0] : acceptedFiles);
    },
    ...rest,
  });

  const inputRecipe = useRecipe({ key: 'input' });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="flex-start"
      _disabled={{ opacity: 0.5, cursor: 'not-allowed' }}
      width="100%"
    >
      <VStack gap={1}>
        {Object.keys(rejectedError).length > 0 && (
          <Code colorPalette="red">
            {Object.values(rejectedError)
              .filter((error): error is string => typeof error === 'string')
              .join(', ')}
          </Code>
        )}

        <Wrap gap={3} align="center" py={2}>
          {fileUpload.acceptedFiles.length > 0 &&
            fileUpload.acceptedFiles.map((file: File) => (
              <Code
                key={`accepted-${file.name}`}
                colorPalette="green"
                variant="outline"
              >
                {file.name}
              </Code>
            ))}
          {fileUpload.rejectedFiles.length > 0 &&
            fileUpload.rejectedFiles.map(
              (e: ChakraFileUpload.FileRejection) => (
                <Code
                  key={`rejected-${e.file.name}`}
                  colorPalette="red"
                  variant="outline"
                >
                  {e.file.name}
                </Code>
              ),
            )}
        </Wrap>
      </VStack>

      <ChakraFileUpload.RootProvider value={fileUpload} {...rest}>
        <ChakraFileUpload.HiddenInput {...inputProps} />

        <InputGroup
          startElement={<LuFileUp />}
          endElement={
            <ChakraFileUpload.ClearTrigger asChild>
              <CloseButton
                me="-1"
                size="xs"
                variant="plain"
                focusVisibleRing="inside"
                focusRingWidth="2px"
                pointerEvents="auto"
              />
            </ChakraFileUpload.ClearTrigger>
          }
        >
          <Input asChild>
            <ChakraFileUpload.Trigger disabled={disabled}>
              <ChakraFileUpload.FileText lineClamp={1} />
            </ChakraFileUpload.Trigger>
          </Input>
        </InputGroup>
        {showList && (
          <FileUploadList showSize={showSize} clearable={clearable} />
        )}
      </ChakraFileUpload.RootProvider>
      {/* {fileUpload.acceptedFiles.length > 0 &&
        children &&
        children({
          files: fileUpload.acceptedFiles,
        })} */}
    </Box>
  );
};
